/**
 * Page de création d'un contact, avec possibilité d'ajouter
 * des To Dos et actions spontanées directement.
 */

import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getContact, createContact, updateContact } from "../api/contactApi";
import { createTodo } from "../api/todoApi";
import { createAction } from "../api/spontaneousActionApi";
import { getTags, createTag } from "../api/tagApi";
import TagSelector from "../components/TagSelector";
import { todayISO } from "../utils/dateUtils";
import "./PageStyles.css";

export default function ContactFormPage() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const [error, setError] = useState("");
  const [allTags, setAllTags] = useState([]);
  const [form, setForm] = useState({
    last_name: "",
    first_name: "",
    company: "",
    job_title: "",
    email: "",
    phone: "",
    comments: "",
    visibility: "public",
  });

  // Inline items to create alongside the contact
  const [pendingTodos, setPendingTodos] = useState([]);
  const [pendingActions, setPendingActions] = useState([]);
  const [showTodoForm, setShowTodoForm] = useState(false);
  const [showActionForm, setShowActionForm] = useState(false);
  const [todoForm, setTodoForm] = useState(emptyTodo());
  const [actionForm, setActionForm] = useState(emptyAction());

  function emptyTodo() {
    return { description: "", due_date: "", stake_level: "", urgency_level: "", status: "in_progress", tag_ids: [] };
  }
  function emptyAction() {
    return { action_date: todayISO(), description: "", tag_ids: [] };
  }

  useEffect(() => {
    getTags().then(setAllTags);
    if (isEdit) loadContact();
  }, [id]);

  async function loadContact() {
    try {
      const data = await getContact(id);
      setForm({
        last_name: data.last_name || "",
        first_name: data.first_name || "",
        company: data.company || "",
        job_title: data.job_title || "",
        email: data.email || "",
        phone: data.phone || "",
        comments: data.comments || "",
        visibility: data.visibility,
      });
    } catch {
      setError("Contact introuvable.");
    }
  }

  function handleChange(e) {
    setForm({ ...form, [e.target.name]: e.target.value });
  }

  async function handleCreateTag(label) {
    const tag = await createTag(label);
    setAllTags([...allTags, tag]);
    return tag;
  }

  function addPendingTodo() {
    setPendingTodos([...pendingTodos, { ...todoForm }]);
    setTodoForm(emptyTodo());
    setShowTodoForm(false);
  }

  function removePendingTodo(index) {
    setPendingTodos(pendingTodos.filter((_, i) => i !== index));
  }

  function addPendingAction() {
    setPendingActions([...pendingActions, { ...actionForm }]);
    setActionForm(emptyAction());
    setShowActionForm(false);
  }

  function removePendingAction(index) {
    setPendingActions(pendingActions.filter((_, i) => i !== index));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    try {
      let contactData;
      if (isEdit) {
        contactData = await updateContact(id, form);
      } else {
        contactData = await createContact(form);
      }

      // Create pending todos and actions for the new contact
      if (!isEdit && contactData?.id) {
        const promises = [];
        for (const t of pendingTodos) {
          promises.push(createTodo({
            contact_id: contactData.id,
            description: t.description,
            due_date: t.due_date || null,
            stake_level: t.stake_level || null,
            urgency_level: t.urgency_level || null,
            status: t.status,
            tag_ids: t.tag_ids,
          }));
        }
        for (const a of pendingActions) {
          promises.push(createAction({
            contact_id: contactData.id,
            action_date: a.action_date || null,
            description: a.description,
            tag_ids: a.tag_ids,
          }));
        }
        await Promise.all(promises);
        navigate(`/contacts/${contactData.id}`);
        return;
      }

      navigate(isEdit ? `/contacts/${id}` : "/contacts");
    } catch {
      setError("Erreur lors de l'enregistrement.");
    }
  }

  return (
    <div className="page">
      <h2>{isEdit ? "Modifier le contact" : "Nouveau contact"}</h2>
      <div className="form-card" style={{ marginTop: "1rem" }}>
        {error && <div className="error-message">{error}</div>}
        <form onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-group">
              <label>Nom</label>
              <input name="last_name" value={form.last_name} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>Prénom</label>
              <input name="first_name" value={form.first_name} onChange={handleChange} />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Société</label>
              <input name="company" value={form.company} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>Fonction</label>
              <input name="job_title" value={form.job_title} onChange={handleChange} />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Email</label>
              <input name="email" type="email" value={form.email} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>Téléphone</label>
              <input name="phone" type="tel" inputMode="tel" autoComplete="tel" value={form.phone} onChange={handleChange} />
            </div>
          </div>
          <div className="form-group">
            <label>Commentaires</label>
            <textarea name="comments" value={form.comments} onChange={handleChange} />
          </div>
          <div className="form-group">
            <label>Visibilité</label>
            <select name="visibility" value={form.visibility} onChange={handleChange}>
              <option value="private">Privé</option>
              <option value="public">Public</option>
            </select>
          </div>

          {/* Section To Dos à créer avec le contact (création uniquement) */}
          {!isEdit && (
            <div className="inline-pending-section">
              <div className="section-header">
                <h4>To Dos</h4>
                <button type="button" className="btn btn-small" onClick={() => setShowTodoForm(!showTodoForm)}>
                  {showTodoForm ? "Annuler" : "+ Ajouter un To Do"}
                </button>
              </div>
              {pendingTodos.map((t, i) => (
                <div key={i} className="pending-item">
                  <span>{t.description || "(sans description)"}</span>
                  <button type="button" className="btn btn-small btn-danger" onClick={() => removePendingTodo(i)}>Retirer</button>
                </div>
              ))}
              {showTodoForm && (
                <div className="inline-form-card">
                  <div className="form-group">
                    <label>Description</label>
                    <textarea value={todoForm.description} onChange={(e) => setTodoForm({ ...todoForm, description: e.target.value })} placeholder="Description du To Do..." />
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Échéance</label>
                      <input type="date" value={todoForm.due_date} onChange={(e) => setTodoForm({ ...todoForm, due_date: e.target.value })} />
                    </div>
                    <div className="form-group" />
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Enjeu</label>
                      <select value={todoForm.stake_level} onChange={(e) => setTodoForm({ ...todoForm, stake_level: e.target.value })}>
                        <option value="">—</option>
                        <option value="A">A — Fort</option>
                        <option value="B">B — Moyen</option>
                        <option value="C">C — Faible</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Urgence</label>
                      <select value={todoForm.urgency_level} onChange={(e) => setTodoForm({ ...todoForm, urgency_level: e.target.value })}>
                        <option value="">—</option>
                        <option value="1">1 — Urgent</option>
                        <option value="2">2 — Normal</option>
                        <option value="3">3 — Peu urgent</option>
                      </select>
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Tags</label>
                    <TagSelector
                      allTags={allTags}
                      selectedIds={todoForm.tag_ids}
                      onChange={(ids) => setTodoForm({ ...todoForm, tag_ids: ids })}
                      onCreateTag={async (label) => { const tag = await handleCreateTag(label); setTodoForm((prev) => ({ ...prev, tag_ids: [...prev.tag_ids, tag.id] })); }}
                    />
                  </div>
                  <button type="button" className="btn btn-primary btn-small" onClick={addPendingTodo}>Ajouter ce To Do</button>
                </div>
              )}
            </div>
          )}

          {/* Section Actions à créer avec le contact (création uniquement) */}
          {!isEdit && (
            <div className="inline-pending-section">
              <div className="section-header">
                <h4>Actions Réalisées</h4>
                <button type="button" className="btn btn-small" onClick={() => setShowActionForm(!showActionForm)}>
                  {showActionForm ? "Annuler" : "+ Ajouter une action"}
                </button>
              </div>
              {pendingActions.map((a, i) => (
                <div key={i} className="pending-item">
                  <span>{a.description || "(sans description)"}</span>
                  <button type="button" className="btn btn-small btn-danger" onClick={() => removePendingAction(i)}>Retirer</button>
                </div>
              ))}
              {showActionForm && (
                <div className="inline-form-card">
                  <div className="form-group">
                    <label>Date</label>
                    <input type="date" value={actionForm.action_date} onChange={(e) => setActionForm({ ...actionForm, action_date: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label>Description</label>
                    <textarea value={actionForm.description} onChange={(e) => setActionForm({ ...actionForm, description: e.target.value })} placeholder="Description de l'action..." />
                  </div>
                  <div className="form-group">
                    <label>Tags</label>
                    <TagSelector
                      allTags={allTags}
                      selectedIds={actionForm.tag_ids}
                      onChange={(ids) => setActionForm({ ...actionForm, tag_ids: ids })}
                      onCreateTag={async (label) => { const tag = await handleCreateTag(label); setActionForm((prev) => ({ ...prev, tag_ids: [...prev.tag_ids, tag.id] })); }}
                    />
                  </div>
                  <button type="button" className="btn btn-primary btn-small" onClick={addPendingAction}>Ajouter cette action</button>
                </div>
              )}
            </div>
          )}

          <div className="form-actions">
            <button type="submit" className="btn btn-primary">
              {isEdit ? "Enregistrer" : "Créer le contact"}
            </button>
            <button type="button" className="btn btn-secondary" onClick={() => navigate("/contacts")}>
              Annuler
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
